import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import ConnectSourceModal from "../../../components/admin/admin-modals/connect-source-modal";
import SourceAttributionTab from "./SourceAttributionTab";

const PROVIDER_COLORS = {
  NAUKRI: "#FF7B1C",
  LINKEDIN: "#0A66C2",
  FOUNDIT: "#E94235",
  INDEED: "#2164F3",
  APNA: "#0066FF",
  AFFINDA: "#7C3AED",
  MANUAL: "#94A3B8",
  CSV: "#64748B",
};
const TABS = ["Search candidates", "Applications", "Job postings", "Source attribution"];

export default function SourceHub() {
  const nav = useNavigate();
  const [tab, setTab] = useState(0);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({
    provider: "ALL",
    minExp: "",
    maxExp: "",
    location: "",
    minCTC: "",
    maxCTC: "",
    notice: "",
    skills: "",
  });
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [profileOpen, setProfileOpen] = useState(null);
  const [applications, setApplications] = useState([]);
  const [postings, setPostings] = useState([]);
  const [postModal, setPostModal] = useState(false);
  const [connectModal, setConnectModal] = useState(false);
  const [connections, setConnections] = useState([]);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    document.title = "Source hub - Ample Hire";
    loadConnections();
  }, []);

  const loadConnections = async () => {
    const tenantId = localStorage.getItem("tenantId");
    try {
      const r = await axiosInstance.get(
        `/common/sources/connections?tenantId=${tenantId}`,
      );
      console.log(r.data);
      setConnections(r.data || []);
    } catch {
      setConnections([]);
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const doSearch = async () => {
    setSearching(true);
    try {
      const r = await axiosInstance.post("/common/sources/search", {
        keywords: query,
        location: filters.location,
        minExperience: filters.minExp ? Number(filters.minExp) : null,
        maxExperience: filters.maxExp ? Number(filters.maxExp) : null,
        minCTC: filters.minCTC || null,
        maxCTC: filters.maxCTC || null,
        noticePeriod: filters.notice || null,
        skills: filters.skills
          ? filters.skills.split(",").map((s) => s.trim())
          : null,
        activelyLooking: null,
        page: 1,
        pageSize: 25,
      });
      setResults(r.data?.candidates || []);
      setTotalCount(r.data?.totalCount || 0);
    } catch {
      setResults([]);
      setTotalCount(0);
    } finally {
      setSearching(false);
    }
  };

  const loadApplications = async () => {
    try {
      const r = await axiosInstance.get("/common/sources/job-board/postings");
      setApplications(r.data || []);
    } catch {
      setApplications([]);
    }
  };

  const loadPostings = async () => {
    try {
      const r = await axiosInstance.get("/common/sources/job-board/postings");
      setPostings(r.data || []);
    } catch {
      setPostings([]);
    }
  };

  const importSelected = async () => {
    const candidates = results
      .filter((c) => selected.has(c.externalId))
      .map((c) => ({
        provider: c.provider,
        externalId: c.externalId,
        email: c.email,
        phone: c.phone,
        firstName: c.firstName,
        lastName: c.lastName,
        company: c.currentCompany,
        title: c.currentTitle,
        location: c.location,
      }));
    try {
      const r = await axiosInstance.post("/common/sources/search/import", {
        candidates,
        tenantId: 1,
      });
      showToast(
        `Imported ${r.data?.imported || 0}, ${r.data?.duplicates || 0} duplicates`,
      );
      setSelected(new Set());
    } catch (e) {
      showToast(e?.message || "Import failed");
    }
  };

  const toggleSelect = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const ScoreBadge = ({ score }) => {
    const color = score >= 80 ? "#10B981" : score >= 60 ? "#F59E0B" : "#EF4444";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: "50%",
          border: `2px solid ${color}`,
          fontSize: 10,
          fontWeight: 700,
          color,
        }}
      >
        {score}
      </span>
    );
  };

  const SourceBadge = ({ provider }) => (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        padding: "1px 6px",
        borderRadius: 4,
        color: "white",
        background: PROVIDER_COLORS[provider] || "#94A3B8",
      }}
    >
      {provider}
    </span>
  );

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">Source hub</h1>
          <p className="text-sm text-vf-muted mt-1">
            Search resumes, view applications, and manage job board postings
          </p>
        </div>
        <button
          onClick={() => setConnectModal(true)}
          className="px-3 py-1.5 rounded bg-vf-primary text-white text-sm font-medium hover:opacity-90 flex items-center gap-1.5"
        >
          <span className="text-lg leading-none">+</span> Connect new source
        </button>
      </div>

      {/* Connected-sources strip */}
      <div className="mb-5 flex flex-wrap gap-2 items-center">
        {connections.length === 0 ? (
          <div className="text-xs text-vf-muted italic">
            No sources connected yet — click <b>Connect new source</b> to wire
            up Naukri, Foundit, LinkedIn, and more.
          </div>
        ) : (
          connections.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-2 px-2.5 py-1 rounded-full border border-slate-200 bg-white text-xs"
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{
                  background: c.status === "CONNECTED" ? "#10B981" : "#CBD5E1",
                }}
                title={c.status}
              />
              <span
                style={{ color: PROVIDER_COLORS[c.provider] || "#475569" }}
                className="font-medium"
              >
                {c.provider}
              </span>
              <span className="text-vf-muted truncate max-w-[140px]">
                {c.displayName || ""}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-4">
        {TABS.map((t, i) => (
          <button
            key={t}
            onClick={() => {
              setTab(i);
              if (i === 1) loadApplications();
              if (i === 2) loadPostings();
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === i ? "border-vf-primary text-vf-primary" : "border-transparent text-vf-muted hover:text-vf-base"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* TAB 0: Search */}
      {tab === 0 && (
        <div>
          <div className="flex gap-2 mb-3">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Search by skills, title, location..."
              className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-vf-accent"
            />
            <button
              onClick={doSearch}
              disabled={searching}
              className="px-4 py-2 rounded bg-vf-primary text-white text-sm font-medium disabled:opacity-50"
            >
              {searching ? "Searching..." : "Search"}
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4 items-center">
            {["ALL", "NAUKRI", "LINKEDIN", "FOUNDIT", "INDEED", "APNA"].map(
              (p) => (
                <button
                  key={p}
                  onClick={() => setFilters((f) => ({ ...f, provider: p }))}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border ${filters.provider === p ? "bg-vf-tint border-vf-highlight text-vf-primary" : "border-slate-200 text-vf-muted hover:bg-vf-tint"}`}
                >
                  {p === "ALL" ? "All sources" : p}
                </button>
              ),
            )}
            <input
              value={filters.minExp}
              onChange={(e) =>
                setFilters((f) => ({ ...f, minExp: e.target.value }))
              }
              placeholder="Min exp"
              className="w-16 px-2 py-1 text-xs border border-slate-200 rounded"
            />
            <span className="text-xs text-vf-muted">–</span>
            <input
              value={filters.maxExp}
              onChange={(e) =>
                setFilters((f) => ({ ...f, maxExp: e.target.value }))
              }
              placeholder="Max"
              className="w-16 px-2 py-1 text-xs border border-slate-200 rounded"
            />
            <input
              value={filters.location}
              onChange={(e) =>
                setFilters((f) => ({ ...f, location: e.target.value }))
              }
              placeholder="Location"
              className="w-28 px-2 py-1 text-xs border border-slate-200 rounded"
            />
            <select
              value={filters.notice}
              onChange={(e) =>
                setFilters((f) => ({ ...f, notice: e.target.value }))
              }
              className="px-2 py-1 text-xs border border-slate-200 rounded"
            >
              <option value="">Notice period</option>
              {["Immediate", "15 Days", "30 Days", "60 Days", "90 Days"].map(
                (n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ),
              )}
            </select>
            <input
              value={filters.skills}
              onChange={(e) =>
                setFilters((f) => ({ ...f, skills: e.target.value }))
              }
              placeholder="Skills (comma-sep)"
              className="w-40 px-2 py-1 text-xs border border-slate-200 rounded"
            />
          </div>

          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div className="bg-vf-tint border border-vf-highlight rounded-lg px-4 py-2 mb-3 flex items-center gap-3">
              <span className="text-xs font-medium text-vf-primary">
                {selected.size} selected
              </span>
              <button
                onClick={importSelected}
                className="px-3 py-1 rounded bg-vf-primary text-white text-xs font-medium"
              >
                Import {selected.size} candidates
              </button>
              <button
                onClick={() => setSelected(new Set())}
                className="text-xs text-vf-muted hover:text-vf-base"
              >
                Clear
              </button>
            </div>
          )}

          {/* Results */}
          <div className="text-xs text-vf-muted mb-2">
            {totalCount} candidates found
          </div>
          <div className="space-y-2">
            {results.map((c) => (
              <div
                key={c.externalId}
                className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3"
              >
                <input
                  type="checkbox"
                  checked={selected.has(c.externalId)}
                  onChange={() => toggleSelect(c.externalId)}
                  className="w-3.5 h-3.5"
                />
                <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
                  {c.firstName?.[0] || ""}
                  {c.lastName?.[0] || ""}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-vf-base truncate">
                      {c.firstName} {c.lastName}
                    </span>
                    <SourceBadge provider={c.provider} />
                  </div>
                  <div className="text-xs text-vf-muted truncate">
                    {c.currentTitle} @ {c.currentCompany}
                  </div>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                      {c.experienceYears}y
                    </span>
                    <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                      {c.location}
                    </span>
                    {c.currentCTC && (
                      <span className="text-[10px] text-vf-muted">
                        {c.currentCTC} → {c.expectedCTC}
                      </span>
                    )}
                    {c.noticePeriod && (
                      <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">
                        {c.noticePeriod}
                      </span>
                    )}
                    {(c.skills || []).slice(0, 5).map((s) => (
                      <span
                        key={s}
                        className="text-[10px] bg-vf-tint text-vf-primary px-1.5 py-0.5 rounded"
                      >
                        {s}
                      </span>
                    ))}
                    {(c.skills || []).length > 5 && (
                      <span className="text-[10px] text-vf-muted">
                        +{c.skills.length - 5}
                      </span>
                    )}
                  </div>
                </div>
                <ScoreBadge score={c.matchScore} />
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => setProfileOpen(c)}
                    className="px-2 py-1 rounded border border-slate-200 text-xs hover:bg-vf-tint"
                  >
                    View
                  </button>
                  <button
                    onClick={() => {
                      toggleSelect(c.externalId);
                      importSelected();
                    }}
                    className="px-2 py-1 rounded bg-vf-primary text-white text-xs"
                  >
                    Import
                  </button>
                </div>
              </div>
            ))}
            {results.length === 0 && !searching && (
              <div className="text-center text-sm text-vf-muted py-12 bg-white border border-dashed border-slate-200 rounded-xl">
                Search across Naukri, Foundit, LinkedIn, and more — enter
                keywords above.
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 1: Applications */}
      {tab === 1 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {applications.length === 0 ? (
            <div className="text-center text-sm text-vf-muted py-12">
              No inbound applications yet. Post jobs to boards to receive
              applications.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-vf-muted uppercase bg-slate-50">
                  <th className="px-4 py-2">Candidate</th>
                  <th className="py-2">Source</th>
                  <th className="py-2">Job</th>
                  <th className="py-2">Applied</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-medium">
                      {a.postedTitle || "—"}
                    </td>
                    <td className="py-2">
                      <SourceBadge provider={a.provider} />
                    </td>
                    <td className="py-2 text-vf-muted">Job #{a.jobId}</td>
                    <td className="py-2 text-vf-muted">
                      {a.postedAt
                        ? new Date(a.postedAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="py-2">
                      <span
                        className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${a.status === "ACTIVE" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
                      >
                        {a.status}
                      </span>
                    </td>
                    <td className="py-2">
                      <button className="text-xs text-vf-primary hover:underline">
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TAB 2: Job Postings */}
      {tab === 2 && (
        <div>
          <div className="flex justify-end mb-3">
            <button
              onClick={() => setPostModal(true)}
              className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium"
            >
              Post job to boards
            </button>
          </div>
          {postings.length === 0 ? (
            <div className="text-center text-sm text-vf-muted py-12 bg-white border border-dashed border-slate-200 rounded-xl">
              No jobs posted to external boards yet.
            </div>
          ) : (
            <div className="space-y-3">
              {postings.map((p) => (
                <div
                  key={p.id}
                  className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center justify-between"
                >
                  <div>
                    <div className="text-sm font-medium text-vf-base">
                      {p.postedTitle || "Job #" + p.jobId}
                    </div>
                    <div className="flex gap-2 mt-1">
                      <SourceBadge provider={p.provider} />
                      <span className="text-xs text-vf-muted">
                        {p.views} views · {p.applicationsReceived} applies
                      </span>
                      <span
                        className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${p.status === "ACTIVE" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
                      >
                        {p.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        axiosInstance
                          .put(
                            `/common/sources/job-board/postings/${p.id}/refresh`,
                          )
                          .then(loadPostings)
                      }
                      className="text-xs text-vf-muted hover:text-vf-base"
                    >
                      Refresh
                    </button>
                    <button
                      onClick={() =>
                        axiosInstance
                          .put(
                            `/common/sources/job-board/postings/${p.id}/close`,
                          )
                          .then(loadPostings)
                      }
                      className="text-xs text-red-600 hover:underline"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Profile slide-over */}
      {profileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex justify-end"
          onClick={() => setProfileOpen(null)}
        >
          <div
            className="bg-white w-full max-w-lg h-full overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setProfileOpen(null)}
              className="text-vf-muted hover:text-vf-base text-xl float-right"
            >
              ×
            </button>
            <h2 className="text-lg font-semibold text-vf-base">
              {profileOpen.firstName} {profileOpen.lastName}
            </h2>
            <p className="text-sm text-vf-muted">
              {profileOpen.currentTitle} @ {profileOpen.currentCompany}
            </p>
            <p className="text-xs text-vf-muted mt-1">
              {profileOpen.location} · {profileOpen.experienceYears}y experience
            </p>
            <div className="mt-4 space-y-3">
              {profileOpen.email && (
                <div className="text-sm">
                  <span className="text-vf-muted">Email:</span>{" "}
                  {profileOpen.email}
                </div>
              )}
              {profileOpen.phone && (
                <div className="text-sm">
                  <span className="text-vf-muted">Phone:</span>{" "}
                  {profileOpen.phone}
                </div>
              )}
              {profileOpen.currentCTC && (
                <div className="text-sm">
                  <span className="text-vf-muted">CTC:</span>{" "}
                  {profileOpen.currentCTC} → {profileOpen.expectedCTC}
                </div>
              )}
              {profileOpen.noticePeriod && (
                <div className="text-sm">
                  <span className="text-vf-muted">Notice:</span>{" "}
                  {profileOpen.noticePeriod}
                </div>
              )}
              {profileOpen.highestDegree && (
                <div className="text-sm">
                  <span className="text-vf-muted">Education:</span>{" "}
                  {profileOpen.highestDegree} — {profileOpen.university} (
                  {profileOpen.gradYear})
                </div>
              )}
              <div>
                <span className="text-xs font-medium text-vf-muted uppercase">
                  Skills
                </span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(profileOpen.skills || []).map((s) => (
                    <span
                      key={s}
                      className="text-xs bg-vf-tint text-vf-primary px-2 py-0.5 rounded"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
              {profileOpen.resumeText && (
                <div>
                  <span className="text-xs font-medium text-vf-muted uppercase">
                    Resume preview
                  </span>
                  <pre className="mt-1 text-xs text-vf-muted bg-slate-50 rounded p-3 max-h-60 overflow-y-auto whitespace-pre-wrap">
                    {profileOpen.resumeText}
                  </pre>
                </div>
              )}
              <div className="flex gap-2 pt-3 border-t border-slate-200">
                <button
                  onClick={() => {
                    toggleSelect(profileOpen.externalId);
                    importSelected();
                    setProfileOpen(null);
                  }}
                  className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium"
                >
                  Import to Ample Hire
                </button>
                <button
                  onClick={() =>
                    nav(
                      `/admin/talent-pools?action=invite&candidateId=${profileOpen.externalId}`,
                    )
                  }
                  className="px-3 py-1.5 rounded border border-slate-300 text-xs"
                >
                  Invite to interview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Post job modal */}
      {postModal && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6"
          onClick={() => setPostModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-vf-base mb-4">
              Post job to boards
            </h2>
            <p className="text-sm text-vf-muted mb-4">
              Select boards and click Post — the job will be published to all
              selected platforms.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {["NAUKRI", "INDEED", "FOUNDIT", "APNA"].map((p) => (
                <label key={p} className="flex items-center gap-1.5 text-xs">
                  <input type="checkbox" defaultChecked />
                  {p}
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPostModal(false)}
                className="px-3 py-1.5 rounded border border-slate-300 text-xs"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  showToast("Job posted to selected boards");
                  setPostModal(false);
                }}
                className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium"
              >
                Post to selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connect new source modal */}
      {connectModal && (
        <ConnectSourceModal
          onClose={() => setConnectModal(false)}
          onSuccess={(conn) => {
            setConnectModal(false);
            showToast(`${conn?.provider || "Source"} connection created`);
            loadConnections();
          }}
        />
      )}

      {tab === 3 && <SourceAttributionTab />}

      {toast && (
        <div className="fixed bottom-6 right-6 z-[9999] px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium bg-vf-base text-white">
          {toast}
        </div>
      )}
    </div>
  );
}
